import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, ShieldAlert, RefreshCcw, ArrowRight } from 'lucide-react';

import { http } from '@/libs/http';

interface MeResponse {
  id: string;
  email: string;
  name: string;
  roles: string[];
  admin: boolean;
  tenant_slug: string | null;
  status: number | null;
}

const STATUS_LABELS: Record<number, string> = {
  0: 'Pending Approval',
  1: 'Active',
  2: 'Inactive',
  3: 'Suspended',
};

async function fetchMe(): Promise<MeResponse> {
  return http.get<MeResponse>('/iam/v1/users/me');
}

export function WaitingRoomPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [meData, setMeData] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    const loadStatus = async (silent = false) => {
      if (!silent) setIsRefreshing(true);
      try {
        const data = await fetchMe();
        if (!isMounted.current) return;
        setMeData(data);
        setError(null);

        // Redirect away only if approved (1)
        if (data.status === 1) {
          navigate(`/t/${slug}`, { replace: true });
        }
      } catch {
        if (!isMounted.current) return;
        setError('Unable to check your status. Please refresh.');
      } finally {
        if (isMounted.current) {
          setIsRefreshing(false);
          setInitialLoading(false);
        }
      }
    };

    void loadStatus();
    const interval = window.setInterval(() => void loadStatus(true), 30_000);

    return () => {
      isMounted.current = false;
      window.clearInterval(interval);
    };
  }, [slug, navigate]);

  const status = meData?.status ?? 0;
  const isApproved = status === 1;
  const isRejected = status === 2 || status === 3;
  const isPending = status === 0;

  return (
    <div className="waiting-room-root">
      {/* Ambient background blobs */}
      <div className="waiting-room-blob waiting-room-blob--1" />
      <div className="waiting-room-blob waiting-room-blob--2" />

      <div className="waiting-room-card">
        {/* Status icon with animated ring */}
        <div className="waiting-room-icon-wrap">
          {isPending && (
            <>
              <div className="waiting-room-ring waiting-room-ring--1" />
              <div className="waiting-room-ring waiting-room-ring--2" />
            </>
          )}
          <div
            className={`waiting-room-icon-circle ${isApproved
              ? 'waiting-room-icon-circle--approved'
              : isRejected
                ? 'waiting-room-icon-circle--rejected'
                : 'waiting-room-icon-circle--pending'
              }`}
          >
            <AnimatePresence mode="wait">
              {isApproved ? (
                <motion.div
                  key="approved"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <CheckCircle2 className="waiting-room-icon" />
                </motion.div>
              ) : isRejected ? (
                <motion.div
                  key="rejected"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <ShieldAlert className="waiting-room-icon" />
                </motion.div>
              ) : (
                <motion.div
                  key="pending"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <svg
                    className="waiting-room-spinner"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle cx="20" cy="20" r="16" stroke="currentColor" strokeOpacity="0.15" strokeWidth="3.5" />
                    <path
                      d="M20 4 A16 16 0 0 1 36 20"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Heading */}
        <div className="waiting-room-header">
          <span className="waiting-room-eyebrow">Access Request</span>
          <AnimatePresence mode="wait">
            <motion.h1
              key={status}
              className="waiting-room-title"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3 }}
            >
              {status === 1
                ? "You're all set!"
                : status === 2
                  ? 'Account Inactive'
                  : status === 3
                    ? 'Account Suspended'
                    : 'Waiting for approval'}
            </motion.h1>
          </AnimatePresence>
          <p className="waiting-room-desc">
            {status === 1
              ? 'Your account has been approved. You will be redirected automatically.'
              : status === 2
                ? 'Your account is currently inactive. Contact your administrator to activate it.'
                : status === 3
                  ? 'Your account has been suspended. Please contact your administrator for assistance.'
                  : 'Your account has been created and is pending review. This page checks automatically every 30 seconds.'}
          </p>
        </div>

        {/* Info row */}
        <div className="waiting-room-info">
          <div className="waiting-room-info-item">
            <span className="waiting-room-info-label">Tenant</span>
            <span className="waiting-room-info-value">/{slug}</span>
          </div>
          {meData?.email && (
            <div className="waiting-room-info-item">
              <span className="waiting-room-info-label">Account</span>
              <span className="waiting-room-info-value">{meData.email}</span>
            </div>
          )}
          <div className="waiting-room-info-item">
            <span className="waiting-room-info-label">Status</span>
            <span
              className={`waiting-room-badge ${isApproved
                ? 'waiting-room-badge--approved'
                : isRejected
                  ? 'waiting-room-badge--rejected'
                  : 'waiting-room-badge--pending'
                }`}
            >
              <span className="waiting-room-badge-dot" />
              {STATUS_LABELS[status] ?? 'Unknown'}
            </span>
          </div>
        </div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              className="waiting-room-error"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="waiting-room-actions">
          {isApproved ? (
            <button
              className="waiting-room-btn waiting-room-btn--primary"
              onClick={() => navigate(`/t/${slug}`, { replace: true })}
            >
              Go to Portal
              <ArrowRight className="waiting-room-btn-icon" />
            </button>
          ) : (
            <button
              className="waiting-room-btn waiting-room-btn--secondary"
              disabled={isRefreshing || initialLoading}
              onClick={async () => {
                setIsRefreshing(true);
                try {
                  const data = await fetchMe();
                  setMeData(data);
                  setError(null);
                  if (data.status === 1) {
                    navigate(`/t/${slug}`, { replace: true });
                  }
                } catch {
                  setError('Unable to check your status. Please try again.');
                } finally {
                  setIsRefreshing(false);
                }
              }}
            >
              <RefreshCcw
                className={`waiting-room-btn-icon ${isRefreshing ? 'waiting-room-btn-icon--spin' : ''}`}
              />
              {isRefreshing ? 'Checking…' : 'Refresh status'}
            </button>
          )}
        </div>

        {/* Auto-check notice */}
        {isPending && !initialLoading && (
          <p className="waiting-room-notice">
            Status is checked automatically every 30 seconds
          </p>
        )}
      </div>

      <style>{`
        .waiting-room-root {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--bg-main);
          padding: 1.5rem;
          position: relative;
          overflow: hidden;
          font-family: var(--font-sans);
          transition: background-color var(--transition-base);
        }

        .waiting-room-blob {
          position: absolute;
          border-radius: 50%;
          filter: blur(80px);
          pointer-events: none;
          z-index: 0;
        }
        .waiting-room-blob--1 {
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(0, 102, 178, 0.08) 0%, transparent 70%);
          top: -100px;
          left: -100px;
        }
        .waiting-room-blob--2 {
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(43, 168, 50, 0.06) 0%, transparent 70%);
          bottom: -80px;
          right: -60px;
        }

        .waiting-room-card {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 480px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 24px;
          padding: 2.5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.75rem;
          box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05);
          transition: background-color var(--transition-base), border-color var(--transition-base);
        }

        .dark .waiting-room-card {
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 0 0 1px rgba(0, 102, 178, 0.06), 0 32px 64px rgba(0,0,0,0.5);
        }

        /* Icon */
        .waiting-room-icon-wrap {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 88px;
          height: 88px;
        }
        .waiting-room-ring {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          border: 1.5px solid rgba(0, 102, 178, 0.35);
          animation: waiting-room-pulse 2.4s ease-in-out infinite;
        }
        .waiting-room-ring--2 {
          inset: -12px;
          border-color: rgba(0, 102, 178, 0.18);
          animation-delay: 0.6s;
        }
        @keyframes waiting-room-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.08); }
        }

        .waiting-room-icon-circle {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 1;
        }
        .waiting-room-icon-circle--pending {
          background: linear-gradient(135deg, rgba(0, 102, 178, 0.18), rgba(43, 168, 50, 0.14));
          border: 1px solid rgba(0, 102, 178, 0.3);
          color: var(--primary);
        }
        .waiting-room-icon-circle--approved {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.18), rgba(5, 150, 105, 0.12));
          border: 1px solid rgba(16, 185, 129, 0.3);
          color: #10b981;
        }
        .waiting-room-icon-circle--rejected {
          background: linear-gradient(135deg, rgba(239, 68, 68, 0.18), rgba(185, 28, 28, 0.1));
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
        }
        .waiting-room-icon {
          width: 28px;
          height: 28px;
        }
        .waiting-room-spinner {
          width: 32px;
          height: 32px;
          animation: waiting-room-spin 1.1s linear infinite;
        }
        @keyframes waiting-room-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Header */
        .waiting-room-header {
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .waiting-room-eyebrow {
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--primary);
          opacity: 0.8;
        }
        .waiting-room-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: var(--secondary-hover);
          letter-spacing: -0.02em;
          line-height: 1.2;
          margin: 0;
        }
        .dark .waiting-room-title {
          color: #f1f5f9;
        }
        .waiting-room-desc {
          font-size: 0.875rem;
          color: var(--secondary);
          line-height: 1.6;
          max-width: 360px;
          margin: 0 auto;
        }

        /* Info */
        .waiting-room-info {
          width: 100%;
          background: var(--bg-main);
          border: 1px solid var(--border-color);
          border-radius: 14px;
          padding: 1rem 1.25rem;
          display: flex;
          flex-wrap: wrap;
          gap: 1rem 2rem;
          align-items: center;
          transition: background-color var(--transition-base), border-color var(--transition-base);
        }
        .waiting-room-info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .waiting-room-info-label {
          font-size: 0.65rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--secondary);
          opacity: 0.7;
        }
        .waiting-room-info-value {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--secondary-hover);
        }
        .dark .waiting-room-info-value {
          color: #e2e8f0;
        }

        /* Badge */
        .waiting-room-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.78rem;
          font-weight: 600;
          padding: 0.3rem 0.7rem;
          border-radius: 9999px;
        }
        .waiting-room-badge--pending {
          background: rgba(0, 102, 178, 0.12);
          color: var(--primary);
          border: 1px solid rgba(0, 102, 178, 0.25);
        }
        .waiting-room-badge--approved {
          background: rgba(16, 185, 129, 0.12);
          color: #10b981;
          border: 1px solid rgba(16, 185, 129, 0.25);
        }
        .waiting-room-badge--rejected {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
          border: 1px solid rgba(239, 68, 68, 0.2);
        }
        .waiting-room-badge-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: currentColor;
          flex-shrink: 0;
        }

        /* Error */
        .waiting-room-error {
          width: 100%;
          background: rgba(239, 68, 68, 0.08);
          border: 1px solid rgba(239, 68, 68, 0.2);
          border-radius: 10px;
          padding: 0.75rem 1rem;
          font-size: 0.82rem;
          color: #ef4444;
          text-align: center;
        }

        /* Actions */
        .waiting-room-actions {
          width: 100%;
          display: flex;
          justify-content: center;
        }
        .waiting-room-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.7rem 1.5rem;
          border-radius: 10px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.18s ease;
        }
        .waiting-room-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .waiting-room-btn--primary {
          background: linear-gradient(135deg, var(--primary), var(--primary-hover));
          color: #fff;
          box-shadow: 0 4px 20px rgba(0, 102, 178, 0.35);
        }
        .waiting-room-btn--primary:hover:not(:disabled) {
          box-shadow: 0 6px 28px rgba(0, 102, 178, 0.5);
          transform: translateY(-1px);
        }
        .waiting-room-btn--secondary {
          background: var(--bg-card);
          color: var(--secondary-hover);
          border: 1px solid var(--border-color);
        }
        .waiting-room-btn--secondary:hover:not(:disabled) {
          background: var(--bg-main);
        }
        .waiting-room-btn-icon {
          width: 15px;
          height: 15px;
        }
        .waiting-room-btn-icon--spin {
          animation: waiting-room-spin 1s linear infinite;
        }

        /* Notice */
        .waiting-room-notice {
          font-size: 0.72rem;
          color: var(--secondary);
          opacity: 0.6;
          text-align: center;
          margin: 0;
        }
      `}</style>
    </div>
  );
}

export default WaitingRoomPage;